'use client'

import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { useBills } from '@/hooks/useBills'
import { useDebounce } from '@/hooks/useDebounce'
import { Bill, Topic } from '@/payload-types'
import { usStates } from '@/utilities/states'

import type { Tab } from '@/components/TabSelect'
import type { MahaApprovalCounts } from '@/utilities/billTrack50/getBills'

export type MahaStance = 'supported' | 'opposed'

export const legislativeTabs: Tab[] = [
  { name: 'All bills', value: 'all-bills' },
  { name: 'Federal', value: 'us' },
  { name: 'State', value: 'state' },
]

interface LegislativeContextType {
  selectedLegislativeTab: (typeof legislativeTabs)[number]['value']
  setSelectedLegislativeTab: (tab: (typeof legislativeTabs)[number]['value']) => void
  selectedState: (typeof usStates)[number]['value'] | undefined
  setSelectedState: (state: (typeof usStates)[number]['value'] | undefined) => void
  searchTerm: string
  setSearchTerm: (searchTerm: string) => void
  isSearching: boolean
  filterOpen: boolean
  setFilterOpen: (filterOpen: boolean) => void
  billTopics: Topic[]
  billTopicCounts: { [key: number]: number }
  mahaApprovalCounts: MahaApprovalCounts

  // API-driven bill data
  bills: Bill[]
  totalBills: number
  billsLoading: boolean
  featuredBills: Bill[]
  featuredBillsLoading: boolean
  allFilteredBills: Bill[]

  // Pagination
  page: number
  setPage: (page: number) => void

  selectedBillTopics: Topic[]
  setSelectedBillTopics: (topics: Topic[]) => void

  mahaStance: MahaStance | null
  setMahaStance: (stance: MahaStance | null) => void
}

const LegislativeContext = createContext<LegislativeContextType | undefined>(undefined)

export const useLegislativeContext = () => {
  const context = useContext(LegislativeContext)
  if (!context) {
    throw new Error('useLegislativeContext must be used within a LegislativeProvider')
  }
  return context
}

type LegislativeProviderProps = {
  children: React.ReactNode
  billTopics: Topic[]
  billTopicCounts: { [key: number]: number }
  mahaApprovalCounts?: MahaApprovalCounts
}

const BILLS_PER_PAGE = 6

const defaultMahaApprovalCounts: MahaApprovalCounts = { supported: 0, opposed: 0 }

export const LegislativeProvider = ({
  children,
  billTopics,
  billTopicCounts,
  mahaApprovalCounts = defaultMahaApprovalCounts,
}: LegislativeProviderProps) => {
  const [selectedLegislativeTab, setSelectedLegislativeTab] =
    useState<(typeof legislativeTabs)[number]['value']>('all-bills')
  const [selectedState, setSelectedState] = useState<
    (typeof usStates)[number]['value'] | undefined
  >(undefined)
  const [selectedBillTopics, setSelectedBillTopics] = useState<Topic[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [filterOpen, setFilterOpen] = useState(false)
  const [mahaStance, setMahaStance] = useState<MahaStance | null>(null)
  const [activeTopicCounts, setActiveTopicCounts] = useState(billTopicCounts)
  const [activeApprovalCounts, setActiveApprovalCounts] = useState(mahaApprovalCounts)

  const debouncedSearchTerm = useDebounce(searchTerm, 300)
  const filterCountAbortRef = useRef<AbortController | null>(null)

  // Derived filter values
  const scope = useMemo(
    () =>
      selectedLegislativeTab === 'us'
        ? 'us'
        : selectedLegislativeTab === 'state'
          ? selectedState?.toLowerCase()
          : undefined,
    [selectedLegislativeTab, selectedState],
  )

  const topicIds = useMemo(() => selectedBillTopics.map((topic) => topic.id), [selectedBillTopics])
  const topicsKey = topicIds.join(',')
  const stanceCountKey = `${scope ?? ''}|${debouncedSearchTerm}|${topicsKey}`
  const lastStanceCountKeyRef = useRef(stanceCountKey)

  const mahaApproved = useMemo(() => {
    if (!mahaStance) return undefined
    return mahaStance === 'supported' ? 'approved' : 'not-approved'
  }, [mahaStance])

  // Pagination — auto-resets to page 1 when filters change
  const filterKey = useMemo(
    () =>
      `${debouncedSearchTerm}-${topicIds.join(',')}-${selectedLegislativeTab}-${selectedState ?? ''}-${mahaApproved ?? ''}`,
    [debouncedSearchTerm, topicIds, selectedLegislativeTab, selectedState, mahaApproved],
  )

  const [pagination, setPagination] = useState({ page: 1, filterKey })
  const page = pagination.filterKey === filterKey ? pagination.page : 1

  const setPage = (newPage: number) => {
    setPagination({ page: newPage, filterKey })
  }

  // Paginated bills for the list view
  const {
    bills,
    totalDocs: totalBills,
    loading: billsLoading,
  } = useBills({
    limit: BILLS_PER_PAGE,
    page,
    search: debouncedSearchTerm || undefined,
    sort: '-bt50.actionDate',
    scope,
    topics: topicIds,
    mahaApproved,
  })

  // Featured bills for the current scope
  const { bills: featuredBills, loading: featuredBillsLoading } = useBills({
    limit: BILLS_PER_PAGE,
    sort: '-bt50.actionDate',
    scope,
    topics: topicIds,
    featuredOnly: true,
    mahaApproved,
  })

  // All matching bills (no pagination) for charts/stats
  const { bills: allFilteredBills } = useBills({
    limit: 0,
    search: debouncedSearchTerm || undefined,
    sort: '-bt50.actionDate',
    scope,
    topics: topicIds,
    mahaApproved,
  })

  const isSearching =
    Boolean(searchTerm.trim()) && (debouncedSearchTerm !== searchTerm || billsLoading)

  const isDefaultFilterCounts =
    !scope && !debouncedSearchTerm.trim() && topicIds.length === 0 && !mahaApproved

  // Back on the unfiltered all-bills view: restore the layout-provided counts
  // and drop in-flight fetch state (the effect cleanup below aborts the request).
  const [prevIsDefaultFilterCounts, setPrevIsDefaultFilterCounts] = useState(isDefaultFilterCounts)
  if (isDefaultFilterCounts !== prevIsDefaultFilterCounts) {
    setPrevIsDefaultFilterCounts(isDefaultFilterCounts)
    if (isDefaultFilterCounts) {
      if (activeTopicCounts !== billTopicCounts) setActiveTopicCounts(billTopicCounts)
      if (activeApprovalCounts !== mahaApprovalCounts) setActiveApprovalCounts(mahaApprovalCounts)
    }
  }

  // Fetch topic and stance counts for the current result set
  useEffect(() => {
    if (isDefaultFilterCounts) {
      lastStanceCountKeyRef.current = stanceCountKey
      return
    }

    if (filterCountAbortRef.current) {
      filterCountAbortRef.current.abort()
    }

    const controller = new AbortController()
    filterCountAbortRef.current = controller

    const fetchFilterCounts = async () => {
      try {
        const params = new URLSearchParams()

        if (debouncedSearchTerm.trim()) {
          params.set('search', debouncedSearchTerm)
        }

        if (scope) {
          params.set('scope', scope)
        }

        if (topicsKey) {
          topicsKey.split(',').forEach((topicId) => params.append('topic', topicId))
        }

        if (mahaApproved) {
          params.set('mahaApproved', mahaApproved)
        }

        const response = await fetch(`/api/frontend/bills/filter-counts?${params.toString()}`, {
          signal: controller.signal,
        })

        if (!response.ok) {
          throw new Error('Failed to fetch bill filter counts')
        }

        const data = await response.json()
        if (!controller.signal.aborted) {
          setActiveTopicCounts(data.topicCounts ?? {})
          if (lastStanceCountKeyRef.current !== stanceCountKey) {
            setActiveApprovalCounts(data.mahaApprovalCounts ?? defaultMahaApprovalCounts)
            lastStanceCountKeyRef.current = stanceCountKey
          }
        }
      } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError') {
          console.error('Bill filter counts fetch error:', error)
        }
      }
    }
    fetchFilterCounts()

    return () => controller.abort()
  }, [isDefaultFilterCounts, debouncedSearchTerm, scope, topicsKey, stanceCountKey, mahaApproved])

  return (
    <LegislativeContext.Provider
      value={{
        selectedLegislativeTab,
        setSelectedLegislativeTab,
        selectedState,
        setSelectedState,
        searchTerm,
        setSearchTerm,
        isSearching,
        filterOpen,
        setFilterOpen,
        billTopics,
        billTopicCounts: activeTopicCounts,
        mahaApprovalCounts: activeApprovalCounts,
        bills,
        totalBills,
        billsLoading,
        featuredBills,
        featuredBillsLoading,
        allFilteredBills,
        page,
        setPage,
        selectedBillTopics,
        setSelectedBillTopics,
        mahaStance,
        setMahaStance,
      }}
    >
      {children}
    </LegislativeContext.Provider>
  )
}
